import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: "website" | "product" | "article";
  noIndex?: boolean;
}

const SITE_NAME = "Alienjuice";
const DEFAULT_DESCRIPTION =
  "Premium auto & motor detailing producten. Professionele snow foam, shampoo, ceramic coating en velgenreiniger. Gratis verzending vanaf €50.";
const DEFAULT_OG_IMAGE = "https://www.alienjuice.nl/og-image.jpg";
const SITE_URL = "https://www.alienjuice.nl";

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  noIndex = false,
}: SEOProps) => {
  const location = useLocation();
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Premium Auto & Motor Detailing Producten`;
  const fullCanonical = canonical ? `${SITE_URL}${canonical}` : `${SITE_URL}${location.pathname}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      
      <link rel="canonical" href={fullCanonical} />
      
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      {fullCanonical && <meta property="og:url" content={fullCanonical} />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="nl_NL" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Hreflang tags voor EU-brede SEO */}
      <link rel="alternate" hrefLang="nl-NL" href={fullCanonical || `${SITE_URL}/`} />
      <link rel="alternate" hrefLang="nl-BE" href={fullCanonical || `${SITE_URL}/`} />
      <link rel="alternate" hrefLang="de-DE" href={fullCanonical || `${SITE_URL}/`} />
      <link rel="alternate" hrefLang="de-AT" href={fullCanonical || `${SITE_URL}/`} />
      <link rel="alternate" hrefLang="de-CH" href={fullCanonical || `${SITE_URL}/`} />
      <link rel="alternate" hrefLang="fr-FR" href={fullCanonical || `${SITE_URL}/`} />
      <link rel="alternate" hrefLang="fr-BE" href={fullCanonical || `${SITE_URL}/`} />
      <link rel="alternate" hrefLang="fr-CH" href={fullCanonical || `${SITE_URL}/`} />
      <link rel="alternate" hrefLang="en-GB" href={fullCanonical || `${SITE_URL}/`} />
      <link rel="alternate" hrefLang="en-IE" href={fullCanonical || `${SITE_URL}/`} />
      <link rel="alternate" hrefLang="x-default" href={fullCanonical || `${SITE_URL}/`} />
      
      {/* Open Graph locale alternates */}
      <meta property="og:locale:alternate" content="nl_BE" />
      <meta property="og:locale:alternate" content="de_DE" />
      <meta property="og:locale:alternate" content="de_AT" />
      <meta property="og:locale:alternate" content="fr_FR" />
      <meta property="og:locale:alternate" content="fr_BE" />
      <meta property="og:locale:alternate" content="en_GB" />
    </Helmet>
  );
};

export default SEO;
